import { convertToDateISOString, formatDateForInput } from "@/utils/date.utils";
import {
  Button, Calendar, Clock, formatPhoneNumberWithCountryCode, Icon, Input, Mail,
  PhoneInput, Save, Select, User
} from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { ProfileImageUploadWrapper } from "../../components/molecules/ProfileImageUpload";
import useFacilityUser from "../../hooks/useFacilityUser";
import { remainingDays, remainingMonths } from "./LicencePage";
import { ProfileSchema, useProfileSchema } from "../../schemas/profile.schema";
import { managerApi } from "../../services/manager.api";
import { secretaryApi } from "../../services/secretary.api";
import {
  getSessionRequest,
  selectSessionAuthState,
} from "../../store/reducers/sessionSlice";
import { selectLicenceState } from "../../store/reducers/licenceSlice";
import { UpdateSecretaryPayload } from "../../types/secretary.definition";
import {
  AcceptedCountriesEnum,
  CivilityEnum,
  CivilityType,
  Location,
  NotificationPrefEnum,
  RoleEnum,
} from "../../types/user.definition";
export const UserProfile: React.FC = () => {
  const { t } = useTranslation();
  const facilityUser = useFacilityUser();
  const { session } = useSelector(selectSessionAuthState);
  const userRole = session?.user?.roleName?.toLowerCase() || "";
  const isManager = userRole === "manager";
  const isSecretary = userRole === "secretary";
  const { licence } = useSelector(selectLicenceState);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const profileValidationSchema = useProfileSchema(
    facilityUser?.phoneNumber
      ? formatPhoneNumberWithCountryCode(facilityUser.phoneNumber)
      : undefined,
  );

  // React Hook Form setup
  const {
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    register,
    reset,
  } = useForm<ProfileSchema>({
    resolver: zodResolver(profileValidationSchema),
    mode: "all",
    criteriaMode: "all",
    reValidateMode: "onChange",
    defaultValues: {
      roleName: facilityUser?.roleName as RoleEnum.MANAGER | RoleEnum.SECRETARY | undefined,
      preferredNotificationMethods:
        facilityUser?.preferredNotificationMethods || [
          NotificationPrefEnum.EMAIL,
        ],
      firstName: facilityUser?.firstName || "",
      lastName: facilityUser?.lastName || "",
      email: facilityUser?.email || "",
      phoneNumber: formatPhoneNumberWithCountryCode(facilityUser?.phoneNumber || ""),
      civility: facilityUser?.civility || CivilityEnum.MR,
      dateOfBirth: formatDateForInput(facilityUser?.dateOfBirth),
      photoUrl: facilityUser?.photoUrl || "",
      locations: facilityUser?.locations || [],
    },
  });

  // Sync form when facilityUser data arrives asynchronously from Redux.
  // defaultValues are frozen at mount; if the session wasn't ready yet,
  // this effect re-populates the form (including the phone country code) once
  // real data becomes available. Only re-runs when phoneNumber changes.
  useEffect(() => {
    if (!facilityUser?.phoneNumber) return;
    reset({
      roleName: facilityUser.roleName as RoleEnum.MANAGER | RoleEnum.SECRETARY | undefined,
      preferredNotificationMethods: facilityUser.preferredNotificationMethods || [NotificationPrefEnum.EMAIL],
      firstName: facilityUser.firstName || "",
      lastName: facilityUser.lastName || "",
      email: facilityUser.email || "",
      phoneNumber: formatPhoneNumberWithCountryCode(facilityUser.phoneNumber),
      civility: facilityUser.civility || CivilityEnum.MR,
      dateOfBirth: formatDateForInput(facilityUser.dateOfBirth),
      photoUrl: facilityUser.photoUrl || "",
      locations: facilityUser.locations || [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityUser?.phoneNumber]);

  const onSubmit = async (formValues: ProfileSchema) => {
    const userId = facilityUser.id;

    // Build diff payload — only include changed fields
    const userData: Record<string, unknown> = {};

    if (formValues.firstName !== facilityUser.firstName) userData.firstName = formValues.firstName;
    if (formValues.lastName !== facilityUser.lastName) userData.lastName = formValues.lastName;
    if (formValues.civility !== facilityUser.civility) userData.civility = formValues.civility;
    if (formValues.phoneNumber !== facilityUser.phoneNumber) userData.phoneNumber = formValues.phoneNumber;
    if (formValues.photoUrl !== facilityUser.photoUrl) userData.photoUrl = formValues.photoUrl;

    const formattedDob = convertToDateISOString(formValues.dateOfBirth as string);
    if (formattedDob !== facilityUser.dateOfBirth) userData.dateOfBirth = formattedDob;

    if (JSON.stringify(formValues.preferredNotificationMethods) !== JSON.stringify(facilityUser.preferredNotificationMethods)) {
      userData.preferredNotificationMethods = formValues.preferredNotificationMethods;
    }

    const formattedLocations = formValues?.locations?.map((loc: Location) => ({
      ...(loc.id && { id: loc.id }),
      address: loc.address || "",
      state: loc.state || "",
      city: loc.city || "",
      zip: loc.zip || "",
      country: loc.country || AcceptedCountriesEnum.SENEGAL,
      residency: loc.residency,
      isPrimary: loc.isPrimary,
      coordinates: loc.coordinates,
    }));
    if (JSON.stringify(formattedLocations) !== JSON.stringify(facilityUser.locations)) {
      userData.locations = formattedLocations;
    }

    try {
      if (isManager) {
        await managerApi.updateManager(String(userId), userData);
      } else if (isSecretary) {
        const payload: Partial<UpdateSecretaryPayload> = {
          ...userData,
          healthcareFacilityId: +facilityUser.healthcareFacility?.id,
        };
        await secretaryApi.updateSecretary(String(userId), payload);
      } else {
        throw new Error(t("profile.errors.invalidRole"));
      }
      reset(formValues);
      toast.success(t("profile.successMessage"));
    } catch (error) {
      toast.error(t("profile.errors.updateFailed"));
    } finally {
      setIsLoading(false);
    }
    dispatch(getSessionRequest());
  };

  return (
    <div className="p-6 space-y-6 animate-fadeIn">
      {/* Main Content */}
      <div
        className={`overflow-hidden bg-white rounded-xl border transition-all duration-700 shadow-soft border-neutral-200`}
        style={{ transitionDelay: "300ms" }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-8">
            {/* User Name and Job Title Section */}
            <div className="flex items-center mb-8 space-x-6">
              <ProfileImageUploadWrapper
                imageUrl={facilityUser?.photoUrl || ""}
                altText={`${facilityUser?.firstName} ${facilityUser?.lastName}`}
                size="lg"
              />
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {facilityUser?.firstName} {facilityUser?.lastName}
                </h3>
                <p className="text-sm text-neutral-600">
                  {isManager
                    ? t("profile.jobTitle.manager")
                    : t("profile.jobTitle.secretary")}
                </p>
                {isManager && licence?.status === "TRIAL" && (
                  <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-medium text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full">
                    <Icon icon={Clock} size="sm" />
                    {remainingMonths(licence.endDate) >= 1
                      ? t("profile.trial.monthsRemaining", { count: remainingMonths(licence.endDate) })
                      : t("profile.trial.daysRemaining", { count: remainingDays(licence.endDate) })}
                  </span>
                )}
              </div>
            </div>

            {/* Personal Information */}
            <div className="space-y-6">
              <div className="flex items-center mb-4 space-x-3">
                <div className="flex justify-center items-center w-8 h-8 rounded-lg bg-primary-500">
                  <Icon icon={User} color="white" size="md" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900">
                  {t("profile.personalInformation")}
                </h3>
              </div>

              {/* Civility and Date of Birth - First Row */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Controller
                    control={control}
                    name="civility"
                    render={({ field }) => (
                      <Select
                        label={t("profile.civility")}
                        value={field.value}
                        onChange={(value) => {
                          const civilityValue = value as CivilityType;
                          field.onChange(civilityValue);
                        }}
                        options={[
                          { value: "MAN", label: t("common.civility.mr") },
                          { value: "WOMAN", label: t("common.civility.mrs") },
                        ]}
                        error={errors.civility?.message}
                      />
                    )}
                  />
                </div>

                <Input
                  label={t("profile.dateOfBirth")}
                  {...register("dateOfBirth")}
                  error={errors.dateOfBirth?.message}
                  size="sm"
                  type="date"
                  placeholder={t("profile.dateOfBirthPlaceholder")}
                  leftIcon={<Calendar size={16} />}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Input
                  required
                  label={t("profile.firstName")}
                  error={errors.firstName?.message}
                  {...register("firstName")}
                  placeholder={t("profile.firstNamePlaceholder")}
                  leftIcon={<User size={16} />}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />

                <Input
                  required
                  label={t("profile.lastName")}
                  error={errors.lastName?.message}
                  placeholder={t("profile.lastNamePlaceholder")}
                  leftIcon={<User />}
                  {...register("lastName")}
                  className="transition-all duration-200 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-1">
                  <Input
                    label={t("profile.email")}
                    {...register("email")}
                    type="email"
                    placeholder={t("profile.emailPlaceholder")}
                    leftIcon={<Mail size={16} />}
                    error={errors.email?.message}
                    className="transition-all duration-200 cursor-not-allowed bg-neutral-100 text-neutral-500 border-neutral-200 focus:ring-2 focus:ring-neutral-300 focus:border-neutral-300"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-neutral-500">
                    {t("profile.emailReadOnly")}
                  </p>
                </div>

                <Controller
                  control={control}
                  name="phoneNumber"
                  render={({ field, fieldState }) => (
                    <PhoneInput
                      label={t("profile.phoneNumber")}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                      placeholder={t("profile.phoneNumberPlaceholder")}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="mt-8 space-y-6">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-1">
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-neutral-700">
                    {t("profile.role")}
                  </label>
                  <Input
                    {...register("roleName")}
                    placeholder={t("profile.rolePlaceholder")}
                    leftIcon={<User />}
                    className="transition-all duration-200 cursor-not-allowed bg-neutral-100 text-neutral-500 border-neutral-200 focus:ring-2 focus:ring-neutral-300 focus:border-neutral-300"
                    readOnly
                    disabled
                  />
                  <p className="text-xs text-neutral-500">
                    {t("profile.roleReadOnly")}
                  </p>
                </div>
              </div>
            </div>

            {/* Address */}
            <div className="mt-8">
              {/* <Controller
                control={control}
                name="locations"
                render={({ field }) => (
                  <LocationForm
                    control={control as Control<ProfileSchema>}
                    setValue={
                      field.onChange as UseFormSetValue<{
                        locations: Location[];
                      }>
                    }
                    errors={errors as FieldErrors<ProfileSchema>}
                  />
                )}
              /> */}
            </div>
          </div>

          {/* Footer */}
          <div className="px-8 py-6 bg-gradient-to-r border-t from-neutral-50 to-neutral-100 border-neutral-200">
            <div className="flex justify-end space-x-3">
              <Button
                type="submit"
                disabled={!isValid || !isDirty || isLoading}
                className="flex items-center hover-scale"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent"></div>
                    <span>{t("profile.saving")}</span>
                  </div>
                ) : (
                  <>
                    <Save className="mr-2 w-4 h-4" />
                    {t("profile.saveChanges")}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
